import { ArrowLeft, FileDown, RefreshCw, Sparkles } from "lucide-react";

import type { DefensePosition } from "../../../shared/domain/defense";
import { NUM_DEF_INNINGS } from "../../../shared/domain/defenseGenerator";
import { buildSitSchedule } from "../../domain/defensePresentation";
import type {
  DefenseInningDocument,
  PlayerListItem,
} from "../../shared/types/contracts";
import { Button } from "@/shared/ui/button";
import { PageHeader } from "@/shared/ui/page-header";
import { SectionCard } from "@/shared/ui/section-card";
import { DefenseAssignmentsEditor } from "./DefenseAssignmentsEditor";
import { DefenseSitSchedule } from "./DefenseSitSchedule";
import { actionKeys } from "../../hooks/actionKeys";
import type { IntentStatusView } from "../../hooks/useIntentStatus";
import { EMPTY_INTENT_STATUS } from "../../hooks/useIntentStatus";

interface DefenseScreenProps {
  players: PlayerListItem[];
  activePlayers: PlayerListItem[];
  preferences: Record<string, string[]>;
  defense: DefenseInningDocument[] | null;
  selectedInning: number;
  gamePitcher: string | null;
  hasActiveGame: boolean;
  onBack: () => void;
  onSetPitcher: (playerId: string) => void;
  onGenerate: () => void;
  onShowExport: () => void;
  onSelectInning: (inning: number) => void;
  onAssign: (
    inning: number,
    position: DefensePosition,
    playerId: string | null,
  ) => void;
  actionStatus?: IntentStatusView;
}

export function DefenseScreen({
  players,
  activePlayers,
  preferences,
  defense,
  selectedInning,
  gamePitcher,
  hasActiveGame,
  onBack,
  onSetPitcher,
  onGenerate,
  onShowExport,
  onSelectInning,
  onAssign,
  actionStatus = EMPTY_INTENT_STATUS,
}: DefenseScreenProps) {
  const pitcherOptions = activePlayers.filter(
    (player) => (preferences[player.id] || [])[0] === "P",
  );
  const sitSchedule = buildSitSchedule(
    activePlayers,
    defense || [],
    gamePitcher,
  );
  const pitcherRequired = pitcherOptions.length > 0 && !gamePitcher;
  const generateLabel = pitcherRequired
    ? "SELECT A PITCHER FIRST"
    : defense
      ? "REGENERATE ROTATION"
      : `GENERATE ${NUM_DEF_INNINGS}-INNING ROTATION`;
  const pitcherPending = actionStatus.countFor(actionKeys.pitcherGroup) > 0;
  const generatePending = actionStatus.isPending(actionKeys.generateDefense);
  const generateUnknown =
    actionStatus.phaseFor(actionKeys.generateDefense) === "outcome_unknown";

  return (
    <div className="mx-auto w-full max-w-4xl space-y-3">
      <PageHeader
        title="DEFENSE"
        eyebrow="Rotation and assignments"
        description="Generate from today’s batting order, then adjust any inning."
        actions={
          <Button
            type="button"
            size="sm"
            variant="outline"
            aria-label={hasActiveGame ? "← GAME" : "← LINEUP"}
            onClick={onBack}
          >
            <ArrowLeft aria-hidden="true" />
            {hasActiveGame ? "GAME" : "LINEUP"}
          </Button>
        }
      />

      <SectionCard
        title={`FROM BATTING ORDER (${activePlayers.length})`}
        description="Edit who’s playing on the Lineup screen; defense uses the same list."
        contentClassName="text-sm"
      >
        <p className="m-0 leading-relaxed">
          {activePlayers.map((player) => player.name).join(", ") ||
            "nobody marked IN"}
        </p>
      </SectionCard>

      <SectionCard
        title="TODAY'S PITCHER (all 9)"
        description={
          pitcherOptions.length === 0
            ? "No designated pitcher is here — P will be filled from the rotation."
            : "Choose the player whose first preference is pitcher."
        }
        contentClassName="pt-1"
      >
        {pitcherOptions.length > 0 ? (
          <div
            className="flex gap-1.5 overflow-x-auto pb-1"
            role="radiogroup"
            aria-label="Today's pitcher"
            aria-busy={pitcherPending || undefined}
          >
            {pitcherOptions.map((player) => (
              <Button
                key={player.id}
                type="button"
                size="sm"
                variant={gamePitcher === player.id ? "selected" : "outline"}
                onClick={() => onSetPitcher(player.id)}
                role="radio"
                aria-checked={gamePitcher === player.id}
                pending={actionStatus.isPending(actionKeys.pitcher(player.id))}
                pendingLabel={
                  actionStatus.phaseFor(actionKeys.pitcher(player.id)) ===
                  "outcome_unknown"
                    ? "CHECKING…"
                    : "SETTING…"
                }
                disabled={pitcherPending}
                className="min-w-20"
              >
                {player.name}
              </Button>
            ))}
          </div>
        ) : null}
      </SectionCard>

      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
        <Button
          type="button"
          size="default"
          disabled={pitcherRequired}
          pending={generatePending}
          pendingLabel={generateUnknown ? "CHECKING…" : "GENERATING…"}
          onClick={() => {
            onGenerate();
            onSelectInning(0);
          }}
          className="min-w-0 whitespace-normal"
        >
          {defense ? (
            <RefreshCw aria-hidden="true" />
          ) : (
            <Sparkles aria-hidden="true" />
          )}
          {generateLabel}
        </Button>
        {defense ? (
          <Button
            type="button"
            size="default"
            variant="outline"
            onClick={onShowExport}
            className="min-w-0"
          >
            <FileDown aria-hidden="true" />
            EXPORT / PRINT GRID
          </Button>
        ) : null}
      </div>

      {defense && defense[selectedInning] ? (
        <>
          <DefenseAssignmentsEditor
            players={players}
            activePlayers={activePlayers}
            preferences={preferences}
            defense={defense}
            selectedInning={selectedInning}
            onSelectInning={onSelectInning}
            onAssign={onAssign}
            actionStatus={actionStatus}
          />
          <DefenseSitSchedule rows={sitSchedule} />
        </>
      ) : null}
    </div>
  );
}
